import type {AppLanguageCode} from '../shared/i18n/app-language';

export type GuideQuestion = {
  answer: string;
  question: string;
};

export type GuideStep = {
  alt: string;
  caption: string;
  image: string;
  intro: string;
  number: string;
  title: string;
};

export type GuidesIndexContent = {
  breadcrumbLabel: string;
  breadcrumbsAriaLabel: string;
  ctaDescription: string;
  ctaEyebrow: string;
  ctaLink: string;
  ctaTitle: string;
  intro: string;
  introEyebrow: string;
  metaDescription: string;
  metaTitle: string;
  principles: readonly string[];
  principlesTitle: string;
  sectionDescription: string;
  sectionTitle: string;
  statusAvailable: string;
  statusPlanned: string;
  title: string;
};

export type HomeInventoryGuideContent = {
  breadcrumbLabel: string;
  breadcrumbsAriaLabel: string;
  ctaDescription: string;
  ctaEyebrow: string;
  ctaLink: string;
  ctaTitle: string;
  headline: string;
  intro: string;
  introEyebrow: string;
  maintainItems: readonly string[];
  maintainTitle: string;
  metaDescription: string;
  metaTitle: string;
  questions: readonly GuideQuestion[];
  questionsTitle: string;
  relatedLink: string;
  relatedTitle: string;
  startIntro: string;
  startItems: readonly string[];
  startTitle: string;
  steps: readonly GuideStep[];
};

export const GUIDES_INDEX_CONTENT: Record<AppLanguageCode, GuidesIndexContent> = {
  en: {
    breadcrumbLabel: 'Guides',
    breadcrumbsAriaLabel: 'Breadcrumbs',
    ctaDescription: 'Create a room, cupboard, or box in Invemory and add the things you want to find easily.',
    ctaEyebrow: 'Start small',
    ctaLink: 'Try it for free',
    ctaTitle: 'Add your first storage place',
    intro: 'Cannot remember what is inside your boxes, cupboards, or pantry? These practical guides help you organize your belongings, find what you need faster, and keep your home inventory useful with Invemory.',
    introEyebrow: 'Order without the busywork',
    metaDescription: 'Practical Invemory guides for organizing household belongings, finding stored items faster, and using QR codes for storage.',
    metaTitle: 'Home organization and inventory guides — Invemory',
    principles: [
      'remember where every item is stored;',
      'check what is inside boxes and shelves without opening them;',
      'use QR codes to open the right inventory list quickly;',
      'keep your home inventory current when things are added or moved.',
    ],
    principlesTitle: 'Invemory keeps the things you own within easy reach',
    sectionDescription: 'We are adding more practical advice about home inventory, QR codes, and convenient storage.',
    sectionTitle: 'Choose where to start',
    statusAvailable: 'Guide available',
    statusPlanned: 'Coming soon',
    title: 'Practical guides for organizing your belongings',
  },
  de: {
    breadcrumbLabel: 'Anleitungen',
    breadcrumbsAriaLabel: 'Brotkrümelnavigation',
    ctaDescription: 'Erstellen Sie in Invemory ein Zimmer, einen Schrank oder eine Kiste und tragen Sie Dinge ein, die Sie schnell wiederfinden möchten.',
    ctaEyebrow: 'Klein anfangen',
    ctaLink: 'Kostenlos ausprobieren',
    ctaTitle: 'Legen Sie Ihren ersten Aufbewahrungsort an',
    intro: 'Sie wissen nicht mehr, was in Kisten, Schränken oder der Vorratskammer liegt? Diese praktischen Anleitungen helfen Ihnen, Ihre Sachen zu ordnen, Gesuchtes schneller zu finden und mit Invemory dauerhaft den Überblick zu behalten.',
    introEyebrow: 'Ordnung ohne unnötigen Aufwand',
    metaDescription: 'Praktische Invemory-Anleitungen, um Haushaltsgegenstände zu organisieren, gelagerte Dinge schneller zu finden und QR-Codes zu nutzen.',
    metaTitle: 'Anleitungen für Hausrat und Aufbewahrung — Invemory',
    principles: [
      'merken Sie sich, wo jeder Gegenstand aufbewahrt wird;',
      'sehen Sie nach, was in Kisten und Regalen liegt, ohne alles auszuräumen;',
      'öffnen Sie mit QR-Codes schnell die passende Inventarliste;',
      'halten Sie Ihr Inventar aktuell, wenn Dinge hinzukommen oder umziehen.',
    ],
    principlesTitle: 'Mit Invemory bleibt Ihr Besitz übersichtlich',
    sectionDescription: 'Nach und nach kommen weitere Tipps zu Hausrat, QR-Codes und praktischer Aufbewahrung hinzu.',
    sectionTitle: 'Wählen Sie Ihren Einstieg',
    statusAvailable: 'Anleitung verfügbar',
    statusPlanned: 'Demnächst',
    title: 'Praktische Anleitungen zum Ordnen Ihrer Sachen',
  },
  uk: {
    breadcrumbLabel: 'Посібники',
    breadcrumbsAriaLabel: 'Навігаційний шлях',
    ctaDescription: 'Створіть в Invemory кімнату, шафу або коробку й додайте речі, які хочете легко знаходити.',
    ctaEyebrow: 'Почніть із малого',
    ctaLink: 'Спробувати безкоштовно',
    ctaTitle: 'Додайте перше місце зберігання',
    intro: 'Не пам’ятаєте, що лежить у коробках, шафах чи коморі? Тут ви знайдете прості поради, які допоможуть упорядкувати речі, швидше знаходити потрібне й підтримувати лад разом з Invemory.',
    introEyebrow: 'Порядок без зайвих зусиль',
    metaDescription: 'Прості поради, які допоможуть упорядкувати речі вдома, швидше знаходити потрібне та зручно користуватися QR-кодами.',
    metaTitle: 'Як упорядкувати речі вдома — посібники Invemory',
    principles: [
      'пам’ятайте, де зберігається кожна річ;',
      'переглядайте вміст коробок і полиць, не перебираючи їх;',
      'використовуйте QR-коди, щоб швидко відкривати потрібний список;',
      'оновлюйте домашній облік, коли додаєте або переміщуєте речі.',
    ],
    principlesTitle: 'Invemory допомагає тримати все під рукою',
    sectionDescription: 'Ми поступово додаємо нові поради про домашній облік, QR-коди та зручне зберігання.',
    sectionTitle: 'Оберіть, з чого почати',
    statusAvailable: 'Доступний посібник',
    statusPlanned: 'Незабаром',
    title: 'Корисні посібники для організації речей',
  },
  ru: {
    breadcrumbLabel: 'Руководства',
    breadcrumbsAriaLabel: 'Навигационная цепочка',
    ctaDescription: 'Создайте в Invemory комнату, шкаф или коробку и добавьте вещи, которые хотите легко находить.',
    ctaEyebrow: 'Начните с малого',
    ctaLink: 'Попробовать бесплатно',
    ctaTitle: 'Добавьте первое место хранения',
    intro: 'Не помните, что лежит в коробках, шкафах или кладовой? Эти практические руководства помогут упорядочить вещи, быстрее находить нужное и поддерживать домашний инвентарь в актуальном состоянии с Invemory.',
    introEyebrow: 'Порядок без лишних усилий',
    metaDescription: 'Практические руководства Invemory по организации домашних вещей, быстрому поиску предметов и использованию QR-кодов для хранения.',
    metaTitle: 'Руководства по учёту и хранению вещей — Invemory',
    principles: [
      'помните, где хранится каждая вещь;',
      'проверяйте содержимое коробок и полок, не перебирая их;',
      'используйте QR-коды, чтобы быстро открыть нужный список;',
      'обновляйте домашний учёт, когда добавляете или перемещаете вещи.',
    ],
    principlesTitle: 'Invemory помогает держать вещи под рукой',
    sectionDescription: 'Мы постепенно добавляем новые советы о домашнем учёте, QR-кодах и удобном хранении.',
    sectionTitle: 'Выберите, с чего начать',
    statusAvailable: 'Руководство доступно',
    statusPlanned: 'Скоро',
    title: 'Практические руководства по организации вещей',
  },
};

export const HOME_INVENTORY_GUIDE_CONTENT: Record<AppLanguageCode, HomeInventoryGuideContent> = {
  en: {
    breadcrumbLabel: 'Home inventory',
    breadcrumbsAriaLabel: 'Breadcrumbs',
    ctaDescription: 'Add your first room or cupboard, then expand the inventory at a pace that works for you.',
    ctaEyebrow: 'Start with one area',
    ctaLink: 'Try Invemory for free',
    ctaTitle: 'Create a map of the things in your home',
    headline: 'How to create a home inventory that stays useful',
    intro: 'When belongings are spread across cupboards, boxes, and different rooms, do not try to catalogue the whole home in one evening. Start with one small area, add it to Invemory, and gradually build a clear map of where things are kept.',
    introEyebrow: 'Order that is easy to maintain',
    maintainItems: [
      'add a new item as soon as you decide on its permanent place;',
      'move it to the correct container in Invemory whenever its location changes;',
      'use the names people use at home: “top drawer” is clearer than an arbitrary number;',
      'expand one room at a time after the first area has already proved useful.',
    ],
    maintainTitle: 'How to keep your inventory current without extra work',
    metaDescription: 'A practical home inventory guide: organize rooms and storage places, add household items, and find what you need quickly with Invemory.',
    metaTitle: 'How to create a practical home inventory — Invemory',
    questions: [
      {question: 'Do I need to add everything at once?', answer: 'No. Start with a few important items and add the rest during regular tidying or whenever you use them.'},
      {question: 'What counts as a container?', answer: 'Any place that makes sense to you: a home, room, cupboard, shelf, drawer, or box.'},
    ],
    questionsTitle: 'Common questions',
    relatedLink: 'See all available Invemory guides',
    relatedTitle: 'Keep improving your home organization',
    startIntro: 'Choose a place where you often search for something: the kitchen, pantry, hallway, or a single cupboard.',
    startItems: [
      'prepare your Invemory account;',
      'identify two to four real storage places;',
      'choose a few things you genuinely want to find faster.',
    ],
    startTitle: 'Where to begin',
    steps: [
      {number: 'Step 1', title: 'Mirror the structure of your home', intro: 'Open Inventory → Containers and create a top-level container for your home. Add rooms or areas inside it. Continue with another level only when useful—for example, Apartment → Kitchen → Top Drawer.', image: 'assets/guides/home-inventory/home-inventory-01-web-en.png', alt: 'Apartment in Invemory with Bedroom, Hallway, Kitchen, and Living Room containers', caption: 'Rooms become clear places you can move between without losing context.'},
      {number: 'Step 2', title: 'Add things where they are actually stored', intro: 'Open the right place and select Add item. Enter a simple name, quantity, and a short note that helps identify it. You do not need to describe every small object—start with supplies, tools, and things that are easy to lose track of.', image: 'assets/guides/home-inventory/home-inventory-02-web-en.png', alt: 'Kitchen in Invemory with a top drawer and a list of household items', caption: 'A name, quantity, and short description provide enough context for everyday use.'},
      {number: 'Step 3', title: 'Find what you need by name', intro: 'When you cannot remember where something is, enter its name in search. Invemory shows the item together with its full storage path—for example, Apartment / Kitchen.', image: 'assets/guides/home-inventory/home-inventory-03-web-en.png', alt: 'Invemory search for Olive Oil Bottle showing the item and the Apartment Kitchen path', caption: 'The path in the search result tells you exactly where to look.'},
    ],
  },
  de: {
    breadcrumbLabel: 'Hausrat erfassen',
    breadcrumbsAriaLabel: 'Brotkrümelnavigation',
    ctaDescription: 'Legen Sie zuerst ein Zimmer oder einen Schrank an und erweitern Sie das Inventar anschließend in Ihrem eigenen Tempo.',
    ctaEyebrow: 'Mit einem Bereich anfangen',
    ctaLink: 'Invemory kostenlos ausprobieren',
    ctaTitle: 'Erstellen Sie eine Übersicht über Ihren Hausrat',
    headline: 'So erstellen Sie ein nützliches Hausinventar',
    intro: 'Wenn Ihre Sachen auf Schränke, Kisten und mehrere Zimmer verteilt sind, müssen Sie nicht an einem Abend den ganzen Haushalt erfassen. Beginnen Sie mit einem kleinen Bereich, tragen Sie ihn in Invemory ein und bauen Sie nach und nach eine übersichtliche Karte Ihrer Aufbewahrungsorte auf.',
    introEyebrow: 'Ordnung, die sich leicht erhalten lässt',
    maintainItems: [
      'tragen Sie einen neuen Gegenstand ein, sobald er einen festen Platz hat;',
      'verschieben Sie ihn in Invemory in den richtigen Behälter, wenn sich sein Aufbewahrungsort ändert;',
      'verwenden Sie vertraute Namen: „obere Schublade“ ist verständlicher als eine beliebige Nummer;',
      'erweitern Sie das Inventar Zimmer für Zimmer, sobald der erste Bereich hilfreich ist.',
    ],
    maintainTitle: 'So bleibt Ihr Inventar ohne Zusatzaufwand aktuell',
    metaDescription: 'Praktische Anleitung für ein Hausinventar: Räume und Aufbewahrungsorte ordnen, Gegenstände erfassen und mit Invemory schnell wiederfinden.',
    metaTitle: 'Ein praktisches Hausinventar erstellen — Invemory',
    questions: [
      {question: 'Muss ich sofort alle Dinge eintragen?', answer: 'Nein. Beginnen Sie mit einigen wichtigen Gegenständen und ergänzen Sie den Rest beim üblichen Aufräumen oder Benutzen.'},
      {question: 'Was gilt als Behälter?', answer: 'Jeder verständliche Ort: die Wohnung, ein Zimmer, ein Schrank, ein Regal, eine Schublade oder eine Kiste.'},
    ],
    questionsTitle: 'Häufige Fragen',
    relatedLink: 'Alle verfügbaren Invemory-Anleitungen ansehen',
    relatedTitle: 'Bringen Sie weiter Ordnung in Ihren Haushalt',
    startIntro: 'Wählen Sie einen Ort, an dem Sie häufig etwas suchen: Küche, Vorratskammer, Flur oder einen einzelnen Schrank.',
    startItems: [
      'bereiten Sie Ihr Invemory-Konto vor;',
      'bestimmen Sie zwei bis vier echte Aufbewahrungsorte;',
      'wählen Sie einige Dinge aus, die Sie wirklich schneller finden möchten.',
    ],
    startTitle: 'Der beste Einstieg',
    steps: [
      {number: 'Schritt 1', title: 'Bilden Sie die Struktur Ihres Zuhauses ab', intro: 'Öffnen Sie Inventar → Container und erstellen Sie einen obersten Container für Ihr Zuhause. Legen Sie darin Zimmer oder Bereiche an. Ergänzen Sie nur bei Bedarf weitere Ebenen, zum Beispiel Wohnung → Küche → Obere Schublade.', image: 'assets/guides/home-inventory/home-inventory-01-web-de.png', alt: 'Wohnung in Invemory mit den Containern Flur, Küche, Schlafzimmer und Wohnzimmer', caption: 'Zimmer werden zu klaren Orten, zwischen denen Sie bequem wechseln können.'},
      {number: 'Schritt 2', title: 'Tragen Sie Dinge an ihrem tatsächlichen Platz ein', intro: 'Öffnen Sie den passenden Ort und wählen Sie Gegenstand hinzufügen. Geben Sie einen einfachen Namen, die Menge und einen kurzen Hinweis zur Erkennung ein. Sie müssen nicht jede Kleinigkeit beschreiben—beginnen Sie mit Vorräten, Werkzeug und Dingen, die leicht aus dem Blick geraten.', image: 'assets/guides/home-inventory/home-inventory-02-web-de.png', alt: 'Küche in Invemory mit oberer Schublade und einer Liste von Haushaltsgegenständen', caption: 'Name, Menge und eine kurze Beschreibung reichen für den täglichen Gebrauch aus.'},
      {number: 'Schritt 3', title: 'Finden Sie Dinge über ihren Namen', intro: 'Wenn Sie nicht mehr wissen, wo etwas liegt, geben Sie den Namen in die Suche ein. Invemory zeigt den Gegenstand mit dem vollständigen Aufbewahrungspfad, zum Beispiel Wohnung / Küche.', image: 'assets/guides/home-inventory/home-inventory-03-web-de.png', alt: 'Suche nach Flasche Olivenöl in Invemory mit dem Pfad Wohnung Küche', caption: 'Der Pfad im Suchergebnis zeigt genau, wo Sie nachsehen müssen.'},
    ],
  },
  uk: {
    breadcrumbLabel: 'Домашній облік речей',
    breadcrumbsAriaLabel: 'Навігаційний шлях',
    ctaDescription: 'Додайте першу кімнату або шафу, а далі розширюйте облік у зручному темпі.',
    ctaEyebrow: 'Почніть із однієї зони',
    ctaLink: 'Спробувати Invemory безкоштовно',
    ctaTitle: 'Створіть свою карту домашніх речей',
    headline: 'Як організувати домашній облік речей',
    intro: 'Якщо речі розходяться по шафах, коробках і різних кімнатах, не намагайтеся описати весь дім за один вечір. Почніть з однієї невеликої зони, додайте її до Invemory і поступово побудуйте зрозумілу карту зберігання.',
    introEyebrow: 'Порядок, який легко підтримувати',
    maintainItems: [
      'додавайте нову річ одразу, коли визначили для неї постійне місце;',
      'після переміщення перенесіть її в правильний контейнер в Invemory;',
      'називайте місця так, як їх називають удома: «верхня шухляда» зрозуміліша за умовний номер;',
      'розширюйте облік кімната за кімнатою, коли перша зона вже стала корисною.',
    ],
    maintainTitle: 'Як підтримувати облік без зайвої роботи',
    metaDescription: 'Практичний посібник зі створення домашнього обліку: організуйте кімнати й місця зберігання, додайте речі та швидко знаходьте потрібне в Invemory.',
    metaTitle: 'Домашній облік речей: як навести лад — Invemory',
    questions: [
      {question: 'Чи потрібно одразу додавати всі речі?', answer: 'Ні. Почніть із кількох важливих предметів, а решту додавайте під час звичного прибирання або користування.'},
      {question: 'Що вважати контейнером?', answer: 'Будь-яке зрозуміле місце: оселю, кімнату, шафу, полицю, шухляду або коробку.'},
    ],
    questionsTitle: 'Поширені запитання',
    relatedLink: 'Перегляньте всі доступні посібники Invemory',
    relatedTitle: 'Продовжуйте наводити лад',
    startIntro: 'Оберіть місце, де ви найчастіше щось шукаєте: кухню, комору, передпокій або одну шафу.',
    startItems: [
      'підготуйте обліковий запис Invemory;',
      'визначте 2–4 реальні місця зберігання;',
      'для першого разу виберіть кілька речей, які справді хочете знаходити швидше.',
    ],
    startTitle: 'З чого почати',
    steps: [
      {number: 'Крок 1', title: 'Повторіть структуру свого дому', intro: 'Відкрийте Інвентар → Контейнери і створіть верхній контейнер для оселі. Усередині додайте кімнати або зони. За потреби продовжуйте рівнями нижче: наприклад, Квартира → Кухня → Верхня шухляда.', image: 'assets/guides/home-inventory/home-inventory-01-web-uk.png', alt: 'Квартира в Invemory з контейнерами Вітальня, Кухня, Передпокій і Спальня', caption: 'Кімнати стають зрозумілими місцями, між якими легко переходити.'},
      {number: 'Крок 2', title: 'Додайте речі там, де вони лежать', intro: 'Відкрийте потрібне місце й натисніть Додати предмет. Вкажіть просту назву, кількість і коротку підказку, яка допоможе впізнати річ. Не потрібно описувати кожну дрібницю: почніть із запасів, інструментів та предметів, які легко загубити з поля зору.', image: 'assets/guides/home-inventory/home-inventory-02-web-uk.png', alt: 'Кухня в Invemory з верхньою шухлядою та переліком домашніх предметів', caption: 'Назва, кількість і короткий опис дають достатньо контексту для щоденного користування.'},
      {number: 'Крок 3', title: 'Знаходьте потрібне за назвою', intro: 'Коли не пам’ятаєте, де лежить річ, введіть її назву в пошуку. Invemory покаже предмет разом із повним шляхом до місця зберігання — наприклад, Квартира / Кухня.', image: 'assets/guides/home-inventory/home-inventory-03-web-uk.png', alt: 'Пошук пляшки оливкової олії в Invemory показує предмет і шлях Квартира Кухня', caption: 'Шлях у результаті пошуку підказує, куди саме йти по річ.'},
    ],
  },
  ru: {
    breadcrumbLabel: 'Домашний учёт вещей',
    breadcrumbsAriaLabel: 'Навигационная цепочка',
    ctaDescription: 'Добавьте первую комнату или шкаф, а затем расширяйте учёт в удобном темпе.',
    ctaEyebrow: 'Начните с одной зоны',
    ctaLink: 'Попробовать Invemory бесплатно',
    ctaTitle: 'Создайте карту вещей в своём доме',
    headline: 'Как организовать домашний учёт вещей',
    intro: 'Если вещи распределены по шкафам, коробкам и разным комнатам, не пытайтесь описать весь дом за один вечер. Начните с одной небольшой зоны, добавьте её в Invemory и постепенно создайте понятную карту хранения.',
    introEyebrow: 'Порядок, который легко поддерживать',
    maintainItems: [
      'добавляйте новую вещь, как только определили для неё постоянное место;',
      'после перемещения переносите её в правильный контейнер в Invemory;',
      'называйте места так, как их называют дома: «верхний ящик» понятнее условного номера;',
      'расширяйте учёт комната за комнатой, когда первая зона уже приносит пользу.',
    ],
    maintainTitle: 'Как поддерживать учёт без лишней работы',
    metaDescription: 'Практическое руководство по домашнему учёту: организуйте комнаты и места хранения, добавляйте вещи и быстро находите нужное в Invemory.',
    metaTitle: 'Домашний учёт вещей: как навести порядок — Invemory',
    questions: [
      {question: 'Нужно ли сразу добавлять все вещи?', answer: 'Нет. Начните с нескольких важных предметов, а остальные добавляйте во время обычной уборки или использования.'},
      {question: 'Что считать контейнером?', answer: 'Любое понятное место: дом, комнату, шкаф, полку, ящик или коробку.'},
    ],
    questionsTitle: 'Частые вопросы',
    relatedLink: 'Посмотреть все доступные руководства Invemory',
    relatedTitle: 'Продолжайте наводить порядок',
    startIntro: 'Выберите место, где вы чаще всего что-то ищете: кухню, кладовую, прихожую или один шкаф.',
    startItems: [
      'подготовьте учётную запись Invemory;',
      'определите два–четыре реальных места хранения;',
      'выберите несколько вещей, которые действительно хотите находить быстрее.',
    ],
    startTitle: 'С чего начать',
    steps: [
      {number: 'Шаг 1', title: 'Повторите структуру своего дома', intro: 'Откройте Инвентарь → Контейнеры и создайте верхний контейнер для дома. Внутри добавьте комнаты или зоны. При необходимости продолжайте уровнями ниже, например Квартира → Кухня → Верхний ящик.', image: 'assets/guides/home-inventory/home-inventory-01-web-ru.png', alt: 'Квартира в Invemory с контейнерами Гостиная, Кухня, Прихожая и Спальня', caption: 'Комнаты становятся понятными местами, между которыми легко переходить.'},
      {number: 'Шаг 2', title: 'Добавьте вещи там, где они лежат', intro: 'Откройте нужное место и нажмите Добавить предмет. Укажите простое название, количество и короткую подсказку, которая поможет узнать вещь. Не обязательно описывать каждую мелочь: начните с запасов, инструментов и предметов, которые легко потерять из виду.', image: 'assets/guides/home-inventory/home-inventory-02-web-ru.png', alt: 'Кухня в Invemory с верхним ящиком и списком домашних предметов', caption: 'Название, количество и короткое описание дают достаточно контекста для ежедневного использования.'},
      {number: 'Шаг 3', title: 'Находите нужное по названию', intro: 'Если не помните, где лежит вещь, введите её название в поиске. Invemory покажет предмет вместе с полным путём к месту хранения, например Квартира / Кухня.', image: 'assets/guides/home-inventory/home-inventory-03-web-ru.png', alt: 'Поиск бутылки оливкового масла в Invemory показывает предмет и путь Квартира Кухня', caption: 'Путь в результате поиска подсказывает, куда именно идти за вещью.'},
    ],
  },
};
